import apiClient from './api';
import type { EquipeDeneignement, User, PaginatedResponse } from '@/types';

const BASE_URL = '/equipes';

export interface CreateEquipeDTO {
  nom: string;
  code: string;
  chefId?: number | null;
  /** Vehicule (MaterielDeneignement) affecte a l'equipe. */
  enginId?: number | null;
}

export interface UpdateEquipeDTO extends Partial<CreateEquipeDTO> {
  actif?: boolean;
}

export interface EquipeStats {
  totalInterventions: number;
  interventionsEnCours: number;
  interventionsTerminees: number;
  tempsMoyenMinutes: number;
}

export interface AgentWithEquipe {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  nomComplet: string;
  equipeActuelle?: {
    id: number;
    nom: string;
  } | null;
  /** Vacation couvrant la date `date` de la reponse (GET .../agents|chefs/all) */
  auPlanningAujourdhui: boolean;
}

/**
 * Service API pour les equipes de deneigement
 */
export const equipeService = {
  /**
   * Liste des equipes. `date` (YYYY-MM-DD, defaut aujourd'hui cote backend)
   * determine pour quel jour chefVacation est evalue - permet d'anticiper
   * une repartition sur un autre jour que aujourd'hui.
   */
  async list(date?: string): Promise<EquipeDeneignement[]> {
    const response = await apiClient.get<EquipeDeneignement[]>(BASE_URL, { params: { date } });
    return response.data;
  },

  /**
   * Detail d'une equipe
   */
  async get(id: number, date?: string): Promise<EquipeDeneignement> {
    const response = await apiClient.get<EquipeDeneignement>(`${BASE_URL}/${id}`, { params: { date } });
    return response.data;
  },

  /**
   * Creation d'une equipe. `date` : jour auquel le chef (si fourni) doit
   * etre au planning (defaut aujourd'hui cote backend).
   */
  async create(data: CreateEquipeDTO, date?: string): Promise<EquipeDeneignement> {
    const response = await apiClient.post<EquipeDeneignement>(BASE_URL, {
      nom: data.nom,
      code: data.code,
      chef_id: data.chefId,
      engin_id: data.enginId,
      date,
    });
    return response.data;
  },

  /**
   * Modification d'une equipe. `date` : jour auquel le nouveau chef (si
   * change) doit etre au planning (defaut aujourd'hui cote backend).
   */
  async update(id: number, data: UpdateEquipeDTO, date?: string): Promise<EquipeDeneignement> {
    const payload: Record<string, unknown> = { date };
    if (data.nom !== undefined) payload.nom = data.nom;
    if (data.code !== undefined) payload.code = data.code;
    if (data.chefId !== undefined) payload.chef_id = data.chefId;
    if (data.enginId !== undefined) payload.engin_id = data.enginId;
    if (data.actif !== undefined) payload.actif = data.actif;

    const response = await apiClient.put<EquipeDeneignement>(`${BASE_URL}/${id}`, payload);
    return response.data;
  },

  /**
   * Suppression d'une equipe
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Equipes disponibles
   */
  async getDisponibles(): Promise<EquipeDeneignement[]> {
    const response = await apiClient.get<EquipeDeneignement[]>(`${BASE_URL}/disponibles`);
    return response.data;
  },

  /**
   * Agents disponibles (non affectes)
   */
  async getAgentsDisponibles(): Promise<User[]> {
    const response = await apiClient.get<User[]>(`${BASE_URL}/agents/disponibles`);
    return response.data;
  },

  /**
   * Tous les agents de deneignement (avec info equipe actuelle et presence
   * au planning de `date`, YYYY-MM-DD - defaut aujourd'hui cote backend)
   */
  async getAllAgents(date?: string): Promise<{ date: string; agents: AgentWithEquipe[] }> {
    const response = await apiClient.get<{ date: string; agents: AgentWithEquipe[] }>(
      `${BASE_URL}/agents/all`,
      { params: { date } }
    );
    return response.data;
  },

  /**
   * Tous les chefs d'equipe (ROLE_CHEF_DENEIGNEMENT, candidats pour chef_id),
   * avec presence au planning de `date`
   */
  async getAllChefs(date?: string): Promise<{ date: string; chefs: AgentWithEquipe[] }> {
    const response = await apiClient.get<{ date: string; chefs: AgentWithEquipe[] }>(
      `${BASE_URL}/chefs/all`,
      { params: { date } }
    );
    return response.data;
  },

  /**
   * Ajouter un agent a une equipe. `date` : jour auquel l'agent doit etre au
   * planning (defaut aujourd'hui cote backend) - permet d'anticiper une
   * affectation sur un autre jour.
   */
  async addAgent(equipeId: number, agentId: number, date?: string): Promise<EquipeDeneignement> {
    const response = await apiClient.post<EquipeDeneignement>(
      `${BASE_URL}/${equipeId}/agents`,
      { agent_id: agentId, date }
    );
    return response.data;
  },

  /**
   * Retirer un agent d'une equipe
   */
  async removeAgent(equipeId: number, agentId: number): Promise<EquipeDeneignement> {
    const response = await apiClient.delete<EquipeDeneignement>(
      `${BASE_URL}/${equipeId}/agents/${agentId}`
    );
    return response.data;
  },

  /**
   * Statistiques d'une equipe
   */
  async getStats(id: number): Promise<EquipeStats> {
    const response = await apiClient.get<EquipeStats>(`${BASE_URL}/${id}/stats`);
    return response.data;
  },
};

export default equipeService;
